import { Fragment, useMemo } from "react";
import { Popover, Transition } from "@headlessui/react";
import { DayPicker } from "react-day-picker";
import CalendarMonth from "../Atomos/Icons/CalendarMonth";
import Close from "../Atomos/Icons/Close";
import { formatDateToString } from "../../utils/functions";
import { useStaffMode } from "../../hooks/StaffModeProvider";
import { staffFieldRoot, staffFieldShell } from "./fieldStyles";
import "../../assets/css/Calendar.css";

type DateLike = Date | string | number | null | undefined;

type SharedProps = {
  title?: string;
  name?: string;
  id?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  errorMessage?: string;
  tooltip?: string;
  clearable?: boolean;
  minDate?: DateLike;
  maxDate?: DateLike;
  className?: string;
  buttonClassName?: string;
  readOnly?: boolean;
  showAsLabel?: boolean;
};

type SingleDatePickerProps = SharedProps & {
  multiple?: false;
  value: DateLike;
  onChange: (value: Date | null) => void;
};

type MultipleDatePickerProps = SharedProps & {
  multiple: true;
  value: DateLike[];
  onChange: (value: Date[]) => void;
};

export type DatePickerProps = SingleDatePickerProps | MultipleDatePickerProps;

const baseInputClasses =
  "w-full rounded-md text-left text-sm transition-colors focus:outline-none";

function startOfDay (date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function parseDateLike (value: DateLike): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : startOfDay(value);
  }
  if (typeof value === "number") {
    const ms = value > 1e12 ? value : value * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : startOfDay(date);
  }
  const mmddyyyy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value).trim());
  if (mmddyyyy) {
    const month = Number(mmddyyyy[1]) - 1;
    const day = Number(mmddyyyy[2]);
    const year = Number(mmddyyyy[3]);
    const date = new Date(year, month, day);
    if (
      !Number.isNaN(date.getTime()) &&
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    ) {
      return startOfDay(date);
    }
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
}

function parseDateList (values: DateLike[] | undefined): Date[] {
  if (!Array.isArray(values)) {
    return [];
  }
  const unique = new Map<number, Date>();
  for (const value of values) {
    const date = parseDateLike(value);
    if (date) {
      unique.set(date.getTime(), date);
    }
  }
  return [...unique.values()].sort((left, right) => left.getTime() - right.getTime());
}

export default function DatePicker (props: DatePickerProps) {
  const {
    title,
    name,
    id,
    required = false,
    placeholder = "MM/DD/YYYY",
    disabled = false,
    error = false,
    errorMessage,
    tooltip,
    clearable = true,
    minDate,
    maxDate,
    className = "",
    buttonClassName = "",
    readOnly = false,
    showAsLabel = false,
  } = props;
  const multiple = props.multiple === true;
  const { isStaffMode } = useStaffMode();
  const selectedValue = props.value;
  const onChangeSingle = !multiple ? props.onChange : undefined;
  const onChangeMultiple = multiple ? props.onChange : undefined;
  const renderAsLabel = readOnly || (showAsLabel && disabled);

  const selectedDates = useMemo(
    () => (multiple ? parseDateList(selectedValue as DateLike[]) : (() => {
      const date = parseDateLike(selectedValue as DateLike);
      return date ? [date] : [];
    })()),
    [multiple, selectedValue],
  );

  const normalizedMinDate = useMemo(
    () => parseDateLike(minDate) ?? undefined,
    [minDate],
  );
  const normalizedMaxDate = useMemo(
    () => parseDateLike(maxDate) ?? undefined,
    [maxDate],
  );

  const fromYear = normalizedMinDate?.getFullYear() ?? new Date().getFullYear() - 20;
  const toYear = normalizedMaxDate?.getFullYear() ?? new Date().getFullYear() + 10;

  const displayValue = useMemo(() => {
    if (selectedDates.length === 0) {
      return "";
    }
    if (selectedDates.length === 1) {
      return formatDateToString(selectedDates[0]);
    }
    if (selectedDates.length === 2) {
      return selectedDates.map((date) => formatDateToString(date)).join(", ");
    }
    return `${formatDateToString(selectedDates[0])} +${selectedDates.length - 1}`;
  }, [selectedDates]);

  const disabledMatchers = useMemo(() => {
    const matchers: Array<{ before: Date } | { after: Date }> = [];
    if (normalizedMinDate) {
      matchers.push({ before: normalizedMinDate });
    }
    if (normalizedMaxDate) {
      matchers.push({ after: normalizedMaxDate });
    }
    return matchers;
  }, [normalizedMaxDate, normalizedMinDate]);

  const handleSingleSelect = (date: Date | undefined) => {
    onChangeSingle?.(date ? startOfDay(date) : null);
  };

  const handleMultipleSelect = (dates: Date[] | undefined) => {
    onChangeMultiple?.((dates ?? []).map(startOfDay));
  };

  const handleClear = (close?: () => void) => {
    if (multiple) {
      onChangeMultiple?.([]);
    } else {
      onChangeSingle?.(null);
    }
    close?.();
  };

  const triggerShell = `${error
    ? "bg-gray-25 outline outline-2 outline-primary"
    : disabled
      ? "bg-gray-100 outline outline-2 outline-gray-200"
      : "bg-gray-25"
    }`;

  if (renderAsLabel) {
    return (
      <div className={`${staffFieldRoot} ${className}`}>
        {title && (
          <label className="font-semibold text-gray-font" title={tooltip}>
            {title}
            {required && <span className="text-primary"> *</span>}
          </label>
        )}
        <p className="py-[11px] text-sm text-gray-font">
          {displayValue || "-"}
        </p>
      </div>
    );
  }

  return (
    <div className={`${staffFieldRoot} ${className}`}>
      {title && (
        <label
          htmlFor={id ?? name}
          title={tooltip}
          className="font-semibold text-gray-font"
        >
          {title}
          {required && <span className="text-primary"> *</span>}
        </label>
      )}

      <Popover className={`relative ${staffFieldShell} ${triggerShell}`}>
        {({ close }) => (
          <>
            {multiple && selectedDates.length > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary p-1 text-xs font-semibold text-white">
                {selectedDates.length}
              </span>
            )}

            <Popover.Button
              id={id ?? name}
              disabled={disabled}
              className={`${baseInputClasses} ${error
                ? "bg-gray-25 text-primary"
                : disabled
                  ? "cursor-not-allowed bg-gray-100 text-gray-400"
                  : "bg-gray-25 text-input-gray"
                } ${buttonClassName}`}
            >
              <div className="relative flex items-center gap-3 pr-6">
                <span
                  className={`block flex-1 truncate ${selectedDates.length ? "text-gray-font" : "text-gray-400"
                    }`}
                >
                  {displayValue || placeholder}
                </span>
                <span
                  className={`absolute right-0 top-1/2 h-5 w-5 -translate-y-1/2 ${disabled ? "text-gray-300" : "text-gray-font"
                    }`}
                >
                  <CalendarMonth />
                </span>
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute z-50 overflow-hidden rounded-md border border-gray-100 bg-white shadow-lg">
                <div className={isStaffMode ? "staff-mode-calendar" : ""}>
                  {multiple ? (
                    <DayPicker
                      mode="multiple"
                      selected={selectedDates}
                      onSelect={handleMultipleSelect}
                      disabled={disabledMatchers}
                      captionLayout="dropdown"
                      fromYear={fromYear}
                      toYear={toYear}
                    />
                  ) : (
                    <DayPicker
                      mode="single"
                      selected={selectedDates[0]}
                      onSelect={(date) => {
                        handleSingleSelect(date);
                        if (date) {
                          close();
                        }
                      }}
                      disabled={disabledMatchers}
                      captionLayout="dropdown"
                      fromYear={fromYear}
                      toYear={toYear}
                    />
                  )}
                </div>
                {clearable && selectedDates.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleClear(close)}
                    className={`m-3 inline-flex items-center gap-1 text-sm font-medium ${isStaffMode
                      ? "text-secondary hover:text-secondary-dark"
                      : "text-primary hover:text-primary-dark"
                      }`}
                  >
                    <Close className="h-4 w-4" />
                    Clear
                  </button>
                )}
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>

      {error && errorMessage && (
        <span className="text-sm text-primary">{errorMessage}</span>
      )}
    </div>
  );
}
